import * as Sentry from '@sentry/browser';

/**
 * Error reporting for the browser, wired the way the server's is.
 *
 * The DSN, environment and release are the server's - read at startup from `/api/v1/client-config`
 * rather than baked in at build time - so a deployment configures Sentry once, in `SENTRY_DSN`, and
 * both applications report into the same project under the same release as the process serving them.
 * A deployment without Sentry answers nothing there, and this installs nothing.
 *
 * What a browser cannot help with is the moment between the page loading and the answer arriving: an
 * error thrown in that window is not reported. It is a few milliseconds on a warm connection, and the
 * alternative - a DSN compiled into every bundle - would be a second place to configure the same thing.
 *
 * Extensions are kept out. A browser extension throwing inside our page shows up as our error and is
 * not, so anything loaded from an extension origin is dropped before it is sent.
 */
const BASE = import.meta.env?.VITE_API_BASE ?? '';

const EXTENSIONS = [
   /extensions\//i,
   /^chrome:\/\//i,
   /^chrome-extension:\/\//i,
   /^moz-extension:\/\//i,
   /^safari-web-extension:\/\//i
];

/**
 * @param {'user' | 'admin'} application Which of the two applications this is, as a tag on every event.
 */
export async function installMonitoring(application)
{
   let config = null;

   try
   {
      const response = await fetch(`${BASE}/api/v1/client-config`);

      config = response.ok ? await response.json() : null;
   }
   catch
   {
      return;
   }

   const sentry = config?.sentry;

   if (!sentry?.dsn)
   {
      return;
   }

   Sentry.init({
      dsn: sentry.dsn,
      environment: sentry.environment ?? undefined,
      release: sentry.release ?? undefined,

      // Errors only. Performance tracing would double the requests every page makes and tell us little
      // about a single-page application that talks to one API.
      tracesSampleRate: 0,
      // SDK 11 collects more by default; keep the previous restricted collection policy.
      dataCollection: {
         userInfo: false,
         cookies: false,
         httpHeaders: {
            request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
            response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] }
         },
         httpBodies: [],
         urlQueryParams: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
         genAI: { inputs: false, outputs: false },
         databaseQueryData: false,
         queues: false,
         graphQL: { document: false, variables: false }
      },
      denyUrls: EXTENSIONS,
      initialScope: { tags: { application } }
   });
}
